import { debounce } from 'lodash';
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import MUIDataTable from "mui-datatables";
import { useNavigate } from "react-router";
import { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { Description } from "@mui/icons-material";
import { configState } from "../../../recoil/atoms";
import { SearchApi } from "../../../api/medical-operations";
import { Button, Grid, CircularProgress } from "@mui/material";
import MainCard from "../../../styles/ui-component/cards/MainCard";
import useAuthorization from "../../Authentication/permissions/Authorization";
import {GetPatientsResponsePatients} from "../../../api/medical-operations/models/get-patients-response-patients";

interface SearchPatientsParams {
  searchKeywords?: any;
  sortOrder?: string;
  sortBy?: string;
  offset?: number;
  limit?: number;
  filterOptions?: any;
}

export default function Balance() {
  const navigate = useNavigate();
  const config = useRecoilValue(configState);

  const [count, setCount] = useState<number>();  
  const [currentPage, setCurrentPage] = useState(0); 
  const [searchQuery, setSearchQuery] = useState(""); 
  const [currentSortBy, setCurrentSortBy] = useState("");
  const [filterOptions, setFilterOptions] = useState({}); 
  const [isLoading, setIsLoading] = useState<boolean>(true);  
  const [currentSortOrder, setCurrentSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [searchResult, setSearchResult] = useState<GetPatientsResponsePatients[]>([]);

  const { canGetBalances, canGetPatientBalance } = useAuthorization();

  const searchPatients = (params: SearchPatientsParams = {}) => {
    const {
      searchKeywords = searchQuery,
      sortOrder = currentSortOrder,
      sortBy = currentSortBy,
      offset = 0,
      limit = 10,
      filterOptions = {}
    } = params;
    
    setIsLoading(true);
    const patient = new SearchApi(config);
    patient
      .searchPatients(searchKeywords, sortOrder, sortBy, offset, limit, { ...filterOptions })
      .then((res) => {              
        setSearchResult(res.data.patients!);
        setCount(res.data.total);     
      })
      .catch((err) => {
        toast.error(err?.response?.data?.detail);
      })
      .finally(() => {
        setIsLoading(false);  
      });
  };

  const handlePageChange = (tableState: any, filterOptions: any) => {
    const { page, rowsPerPage } = tableState;

    setCurrentPage(page);
    
    const newOffset = page * rowsPerPage;

    searchPatients({
      offset: newOffset,
      limit: rowsPerPage,
      filterOptions: filterOptions
    });
  };

  const handleChangeRowsPerPage = (tableState: any, filterOptions: any) => {
    
    const { page, rowsPerPage } = tableState;
    searchPatients({            
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      filterOptions: filterOptions
    });      
  };

  const handleSort = (tableMeta: any, filterOptions: any) => {
    const { rowsPerPage } = tableMeta;

    const columnName = tableMeta.activeColumn;
    const columnSortMap: { [key: number]: string } = {
      1: 'first_name',
      2: 'middle_name',
      3: 'family_name'      
    };
    const sort_by = columnSortMap[columnName] || '';

    const newOffset = currentPage * rowsPerPage;
    const newSortOrder: "ASC" | "DESC" = currentSortOrder === "ASC" ? "DESC" : "ASC";
    
    searchPatients({      
      sortOrder: newSortOrder,
      sortBy: sort_by,
      offset: newOffset,
      limit: rowsPerPage,
      filterOptions: filterOptions
    });    
    setCurrentSortBy(sort_by);
    setCurrentSortOrder(newSortOrder); 
  };

  const handleFilter = debounce((tableMeta: any) => {
    const { filterList } = tableMeta;
    
    const newFilterOptions = {
      params: {}
    };
  
    const filterMappings = {
      1: "FirstName",
      2: "MiddleName",
      3: "FamilyName"          
    };
  
    filterList.forEach((filterValues: string[], index: number) => {
      const selectedValue = filterValues[0];
      if (selectedValue) {
        const parameterName = filterMappings[index];
  
        if (!newFilterOptions.params.hasOwnProperty(parameterName)) {
          newFilterOptions.params[parameterName] = selectedValue;
        } else {
          newFilterOptions.params[parameterName] += `,${selectedValue}`;
        }
      }
    });
    setFilterOptions(newFilterOptions);
    handleSort(tableMeta, newFilterOptions);
    handlePageChange(tableMeta, newFilterOptions);
    handleChangeRowsPerPage(tableMeta, newFilterOptions);
  }, 600);  

  const handleSearch = debounce((searchQuery: string) => {       
    setSearchQuery(searchQuery);
  }, 500);

  useEffect(() => {        
    searchPatients({ searchKeywords: searchQuery });
    // eslint-disable-next-line react-hooks/exhaustive-deps    
  }, [searchQuery]);

  const columns = [
    {
      name: "PatientID",
      options: {
        display: false,
        filter: false,
        print: false,
        download: false,
      },
    },

    {
      name: "FirstName",
      label: "First Name",
      options: {
        filter: true,
      },
    },
    {
      name: "MiddleName",
      label: "Middle Name",
      options: {
        filter: true,
      },
    },
    {
      name: "FamilyName",
      label: "Last Name",
      options: {
        filter: true,
      },
    },
    {
      name: "Balance",
      label: "Balance",
      options: {
        filter: false,
      },
    },
    {
      name: "action",
      label: "Statement",
      print: false,
      options: {
        filter: false,
        sort: false,
        print: false,
        download: false,
        setCellHeaderProps: () => ({ align: "center" }),
        setCellProps: () => ({ align: "center" }),
        customBodyRender: (value: any, tableMeta: any, updateValue: any) => (
          <>
          {canGetPatientBalance && (
            <Tooltip title="Balance Statement">
              <Button
                color="secondary"
                onClick={() => {
                  navigate(`/patient/${tableMeta.rowData[0]}/balance`, {
                    state: tableMeta.tableData[tableMeta.rowIndex],
                  });
                }}
              >
                <Description color="secondary" />
              </Button>
            </Tooltip>
          )}
          </>
        ),
      },
    },
  ];

  return (
    <>
      {canGetBalances &&(
        <MainCard>
          <Grid>
              <>                
                <MUIDataTable
                  data={isLoading ? [] : searchResult}                              
                  columns={columns}
                  options={{
                    serverSide: true,
                    count: count,
                    filter: true,
                    viewColumns: false,
                    selectableRows: "none",
                    selectableRowsOnClick: false,
                    elevation: 0,
                    rowsPerPageOptions : [10,30,50,100],                    
                    filterType: "textField",                                      
                    enableNestedDataAccess: ".",
                    responsive: "standard",
                    jumpToPage: false,
                    textLabels: {
                      body: {
                        noMatch: isLoading ? <CircularProgress/> : 'Sorry there is no matching data'
                      }
                    },                                                                                                                    
                    onTableChange: async (action, tableMeta) => {                                                                                                                                           
                      switch (action) {
                        case "changePage":
                          handlePageChange(tableMeta, filterOptions);
                          break;
                        case "changeRowsPerPage":
                          handleChangeRowsPerPage(tableMeta, filterOptions);
                          break;
                        case "sort":
                          handleSort(tableMeta, filterOptions);
                          break;
                        case "filterChange":
                          handleFilter(tableMeta);                          
                          break;
                        case "search":
                          const searchText: string = tableMeta.searchText || "";
                          handleSearch(searchText);
                          break;   
                        case "resetFilters":
                          searchPatients();                         
                          break;
                        default:                          
                          break;
                      }                   
                    }                  
                  }}
                  title=""
                />                
              </>
          </Grid>
        </MainCard>
      )}
    </>
  );
}
